import { useCallback, useEffect, useState } from "react";

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function readStoredSize(key, fallback, min, max) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    const n = Number(raw);
    if (Number.isFinite(n)) return clamp(n, min, max);
  } catch {
    /* ignore */
  }
  return fallback;
}

/** Persisted panel width (px) for admin resizable splits. */
export function usePersistedPanelSize(key, { defaultSize, minSize, maxSize }) {
  const [size, setSize] = useState(() => readStoredSize(key, defaultSize, minSize, maxSize));

  useEffect(() => {
    setSize(readStoredSize(key, defaultSize, minSize, maxSize));
  }, [key, defaultSize, minSize, maxSize]);

  const setClampedSize = useCallback(
    (next) => {
      setSize((prev) => {
        const value = typeof next === "function" ? next(prev) : next;
        return clamp(value, minSize, maxSize);
      });
    },
    [minSize, maxSize],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, String(size));
    } catch {
      /* ignore */
    }
  }, [key, size]);

  const reset = useCallback(() => {
    setClampedSize(defaultSize);
  }, [defaultSize, setClampedSize]);

  return { size, setClampedSize, reset, minSize, maxSize, defaultSize };
}
